import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Loader2, Plus, Trash2, Pencil, X } from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from '../../../hooks/use-toast';
import { ModuleShell } from './ModuleShell';

const EMPTY = { title: '', slug: '', body: '', published: false };

const Editor = ({ draft, setDraft, onSave, onCancel, saving, itemLabel }) => (
  <Card className="bg-gray-800 border-gray-700 p-6 space-y-3" data-testid="content-editor">
    <div className="flex justify-between items-center">
      <p className="text-white font-semibold">{draft.id ? `Editar ${itemLabel}` : `Nuevo ${itemLabel}`}</p>
      <button onClick={onCancel} className="text-gray-400 hover:text-white" data-testid="content-cancel-btn"><X className="w-4 h-4" /></button>
    </div>
    <Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Título *" className="bg-gray-700 border-gray-600 text-white" data-testid="content-title-input" />
    <Input value={draft.slug} onChange={e => setDraft({ ...draft, slug: e.target.value })} placeholder="URL amigable (ej. terminos-y-condiciones)" className="bg-gray-700 border-gray-600 text-white" data-testid="content-slug-input" />
    <Textarea value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={8} placeholder="Contenido" className="bg-gray-700 border-gray-600 text-white" data-testid="content-body-input" />
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <Switch checked={draft.published} onCheckedChange={v => setDraft({ ...draft, published: v })} data-testid="content-published-toggle" /> Publicado
      </label>
      <Button onClick={onSave} disabled={saving || !draft.title.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="content-save-btn">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Guardar
      </Button>
    </div>
  </Card>
);

export const ContentModulePage = ({ kind, title, description, note, itemLabel }) => {
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { adminAPI.listContent(kind).then(setItems).catch(() => setItems([])); }, [kind]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { id, title: t, slug, body, published } = draft;
      const payload = { title: t, slug, body, published };
      if (id) await adminAPI.updateContent(kind, id, payload); else await adminAPI.createContent(kind, payload);
      toast({ title: 'Guardado' });
      setDraft(null); load();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.title}"?`)) return;
    await adminAPI.deleteContent(kind, item.id);
    load();
  };

  return (
    <ModuleShell title={title} description={description} note={note} testid={`module-${kind}`}>
      {draft ? (
        <Editor draft={draft} setDraft={setDraft} onSave={save} onCancel={() => setDraft(null)} saving={saving} itemLabel={itemLabel} />
      ) : (
        <Button onClick={() => setDraft({ ...EMPTY })} className="bg-orange-500 hover:bg-orange-600" data-testid="content-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo {itemLabel}
        </Button>
      )}
      <Card className="bg-gray-800 border-gray-700 divide-y divide-gray-700" data-testid="content-list">
        {!items ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          : items.length === 0 ? <p className="p-6 text-gray-500 text-sm">Aún no hay contenido.</p>
          : items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-3 p-4" data-testid={`content-item-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{it.title}</p>
                <p className="text-xs text-gray-500">{it.slug ? `/${it.slug} · ` : ''}{new Date(it.updated_at).toLocaleDateString('es-MX')}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${it.published ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-700 text-gray-400'}`}>
                {it.published ? 'Publicado' : 'Borrador'}
              </span>
              <button onClick={() => setDraft({ ...EMPTY, ...it })} className="text-gray-400 hover:text-white p-1" data-testid={`content-edit-${i}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(it)} className="text-gray-400 hover:text-red-400 p-1" data-testid={`content-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
      </Card>
    </ModuleShell>
  );
};
